/**
 * Ersatz fuer `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch
 * `hydrateIcons` (`index.html:4021`).
 *
 * ================= Was das Original tut =================
 *
 * `icon('arrow-right')` liefert nur einen Platzhalter: `<i data-lucide="arrow-right"
 * aria-hidden="true"></i>`. Erst `hydrateIcons()` ersetzt ihn ueber die per `<script defer>`
 * geladene UMD-Fassung von lucide (`index.html:3631`, Version **0.468.0**) durch das eigentliche
 * `<svg class="lucide lucide-arrow-right" …>` - mit `stroke-width: 1.8` statt der Voreinstellung 2
 * (`createIcons({ attrs: { 'stroke-width': 1.8 } })`).
 *
 * Die Baselines halten genau dieses ERGEBNIS fest, nicht den Platzhalter: `board-standard` enthaelt
 * `svg.lucide.lucide-combine` mit vier `path`- und zwei `rect`-Kindern, `payments-standard`
 * enthaelt `svg.lucide.lucide-x` mit zwei `path`-Kindern. Ein portiertes `<i>` waere deshalb ein
 * Fehlschlag in Signatur UND Bild - der Platzhalter selbst ist nie sichtbar.
 *
 * ================= Der Weg hier =================
 *
 * `lucide-react@0.468.0` (dieselbe Version, bereits Abhaengigkeit in `frontend/package.json`)
 * liefert je Symbol eine Komponente, die genau dieses `<svg class="lucide lucide-…">` erzeugt.
 * `strokeWidth={1.8}` wird an jedem Aufruf gesetzt, damit die Linienstaerke der des Originals
 * entspricht.
 *
 * ================= BEFUND: Zifferngeschwaenzte Symbolnamen brechen den Klassennamen ==========
 *
 * lucide-react bildet den Klassennamen aus dem KOMPONENTENNAMEN:
 * `mergeClasses("lucide-" + toKebabCase(iconName), className)`, mit
 * `toKebabCase = s => s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase()`
 * (`node_modules/lucide-react/dist/esm/{createLucideIcon,shared/src/utils}.js`).
 *
 * Bei einem Namen, der auf eine ZIFFER endet, greift dieses Muster nicht: aus `Clock3` wird
 * `clock3`, also `class="lucide lucide-clock3"`; aus `Trash2` wird `trash2`. Die UMD-Fassung im
 * Original bildet den Klassennamen dagegen aus dem DATEI-/Symbolnamen `clock-3` bzw. `trash-2` und
 * schreibt `class="lucide lucide-clock-3"`. Belegt an den eingecheckten Baselines:
 * `board-standard` enthaelt `lucide-columns-3` und `lucide-settings-2`, `board-listeneditor`
 * zusaetzlich `lucide-trash-2`, und `payments-standard` fuehrt `svg.lucide.lucide-trash-2` zweimal
 * (Knoten 317 und 401 des `.ov-wrap`-Ausschnitts) - keiner dieser Klassennamen kaeme aus
 * `lucide-react` heraus.
 *
 * Ein `className`-Zusatz repariert das nicht: `mergeClasses` HAENGT AN, es ersetzt nicht - das
 * Ergebnis waere `lucide lucide-clock3 lucide-clock-3`, also erst recht falsch.
 *
 * Deshalb: Symbole mit Ziffernende gehen ueber die generische `Icon`-Komponente von lucide-react
 * mit woertlich uebernommenem `iconNode` (aus derselben Paketversion) - so steht der Klassenname
 * frei zur Verfuegung. Ein Testfall in `test/komponenten/Board/boardSeite.test.tsx` haelt fuer
 * `settings-2` und `columns-3` fest, dass die Regel greift.
 *
 * ================= Herkunft dieses Moduls und der Merge-Stand =================
 *
 * Angelegt in Task 11 (Phase 6) unter `components/OverviewPage/`, mit dem ausdruecklichen Vermerk,
 * es beim naechsten Verwender hierher hochzuziehen. Task 13 (Bestellboard) ist dieser Verwender:
 * ohne die Ziffernregel truegen `settings-2` und `columns-3` in der Werkzeugleiste still falsche
 * Klassennamen. Weil die Phase-6-Tasks parallel in mehreren Zweigen liefen, legten Task 22
 * (Zahlungsseite), Task 24 (Teil-/Sammellieferung) und Task 26 (Nutzerverwaltung) das Modul unter
 * demselben Pfad mit derselben Schnittstelle noch einmal an.
 *
 * Beim Merge von `feat/09-spa-store` in `feat/09-react-spa` wurde der faellige add/add-Konflikt als
 * **Vereinigung beider Registraturen** aufgeloest, nicht durch Wahl einer Seite - eine Seite zu
 * nehmen bricht die Symbole der anderen STILL: kein Test fehlt, `Ikone` gibt fuer den unbekannten
 * Namen einfach `null` zurueck, es fehlt nur ein Bild. Gezaehlt: react-spa 15 + 1, store 34 + 4,
 * Schnittmenge 5 + 1 -> Vereinigung **44 KOMPONENTEN + 4 ZIFFERN_SYMBOLE**. Die Herkunft je Block
 * ist unten vermerkt; Schnittstelle (`IkoneProps`), `STRICHSTAERKE` und der Rumpf von `Ikone` waren
 * in beiden Fassungen gleich.
 *
 * Die Vorhersage traf zu: `feat/09-spa-seiten` und `feat/09-spa-ek` brachten **keine** zweite
 * Registratur, sondern **16 lokale Platzhalterfunktionen**, die nur `<i data-lucide="…">` erzeugten -
 * in der SPA hydriert das niemand (die UMD-Fassung laeuft ausschliesslich im Baseline-Rundlauf des
 * Playwright-Harness, `harness.ts:218`), es entstand eine leere 18x18-Flaeche (`bestand.css:3581`).
 * Auf `feat/09-spa-ikonen` sind diese 16 Dateien auf DIESE Registratur gezogen; die 15 Symbole, die
 * ihnen fehlten, stehen unten im letzten Block. Gezaehlt: **59 KOMPONENTEN + 4 ZIFFERN_SYMBOLE**.
 *
 * ⚠ Wer ein Symbol aus `KOMPONENTEN` entfernt, bekommt hier **keinen** Fehler: `Ikone` gibt fuer
 * einen unbekannten Namen `null` zurueck (siehe unten). Was das faengt, sind die
 * `Symbolnamen`-Faelle der Struktur-Tests (`test/komponenten/Drawer/*Struktur.test.tsx`,
 * `OverviewPage/uebersichtStruktur.test.tsx`): sie vergleichen die `lucide-…`-Klassen des
 * gezeichneten Baums gegen die der Baseline und fallen namentlich, sobald ein Symbol fehlt.
 */
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  Banknote,
  Blocks,
  Calculator,
  CalendarClock,
  Check,
  ChevronsDown,
  ChevronsRight,
  Circle,
  CircleCheck,
  CircleCheckBig,
  CircleDashed,
  CircleMinus,
  CirclePlus,
  ClipboardList,
  ClipboardPen,
  Combine,
  CreditCard,
  Database,
  Eraser,
  ExternalLink,
  Factory,
  FileSpreadsheet,
  FileText,
  Files,
  Filter,
  FilterX,
  GitFork,
  GripVertical,
  History,
  Icon,
  Image as ImageSymbol,
  Info,
  KeyRound,
  Link,
  List,
  ListChecks,
  ListOrdered,
  LogOut,
  Mail,
  PackageCheck,
  Plane,
  ReceiptText,
  RefreshCw,
  Save,
  ScanSearch,
  ScanText,
  Search,
  Send,
  ShieldCheck,
  Ship,
  Split,
  TriangleAlert,
  Truck,
  Unlink,
  Upload,
  UserCheck,
  UserPlus,
  UserX,
  Warehouse,
  X,
  type IconNode,
  type LucideProps,
} from "lucide-react";

/** `createIcons({ attrs: { 'stroke-width': 1.8 } })`, index.html:4024. */
const STRICHSTAERKE = 1.8;

/**
 * Symbole, deren Komponentenname keine Ziffer am Ende hat - hier stimmt der von lucide-react
 * gebildete Klassenname mit dem der UMD-Fassung ueberein.
 */
const KOMPONENTEN: Record<string, React.ComponentType<LucideProps>> = {
  // Uebersichtsseite (Task 11)
  "arrow-right": ArrowRight,
  "calendar-clock": CalendarClock,
  "circle-check-big": CircleCheckBig,
  "credit-card": CreditCard,
  database: Database,
  files: Files,
  "list-checks": ListChecks,
  "refresh-cw": RefreshCw,
  "triangle-alert": TriangleAlert,
  // Bestellboard (Task 13): Werkzeugleiste, Spaltenkoepfe, Gruppenzeilen
  "arrow-down": ArrowDown,
  "arrow-up": ArrowUp,
  "arrow-up-down": ArrowUpDown,
  "chevrons-down": ChevronsDown,
  "chevrons-right": ChevronsRight,
  combine: Combine,
  filter: Filter,
  "filter-x": FilterX,
  "package-check": PackageCheck,
  // Spalteneditoren (Task 14): Dialogkopf, Zeilenknoepfe und Fuss beider Editoren.
  // `arrow-right` steht bereits oben; `trash-2` steht unten (Ziffernregel).
  "arrow-left": ArrowLeft,
  "circle-plus": CirclePlus,
  save: Save,
  x: X,
  // Statussymbole (`STATUS_ICONS`, config/board.ts). `circle-check-big` steht bereits oben.
  "clipboard-pen": ClipboardPen,
  factory: Factory,
  list: List,
  "shield-check": ShieldCheck,
  ship: Ship,
  split: Split,
  // Split-Kennzeichnung (`SplitMarke.tsx`): Quelle, die in Teile aufgeteilt wurde.
  "git-fork": GitFork,
  warehouse: Warehouse,
  // Kontextmenue der Bestellzeile (Task 15): `openOrderContextMenu`, index.html:7145-7169.
  // `split`, `circle-plus`, `credit-card`, `list-checks`, `files`, `refresh-cw`, `arrow-right`
  // stehen bereits oben; `trash-2` unten (Ziffernregel).
  archive: Archive,
  "archive-restore": ArchiveRestore,
  calculator: Calculator,
  "clipboard-list": ClipboardList,
  "external-link": ExternalLink,
  // Zahlungsseite (Task 22): Rechnermaske, Guthaben-Tasks, Sicherungsliste.
  // `circle-check-big`, `save` und `x` stehen bereits oben; `trash-2` unten (Ziffernregel).
  eraser: Eraser,
  link: Link,
  "receipt-text": ReceiptText,
  unlink: Unlink,
  upload: Upload,
  // Nutzerverwaltung (Task 26): Konto-Aktionen, Anlegen-Formular, Aktiv/Inaktiv-Umschalter
  "key-round": KeyRound,
  "log-out": LogOut,
  "user-plus": UserPlus,
  "user-x": UserX,
  "user-check": UserCheck,
  // Teil-/Sammellieferung (Task 24): primaere Knoepfe der beiden neuen Dialoge - `combine` und
  // `split` stehen bereits oben (Task 13 bzw. Statussymbole), es kommt hier nichts hinzu.
  //
  // ============ Bestell-Drawer und Zahlungsansicht-Variante (Tasks 16-21 bzw. 12) ==============
  // Nachgetragen beim Zusammenlegen der 16 lokalen Platzhalter-`Ikone` auf diese Registratur
  // (Zweig `feat/09-spa-ikonen`, Vorbefund `merge-seiten-ek.md` Abschnitt 3b). Die 16 Dateien nennen
  // 42 verschiedene Symbole; 27 standen schon oben, diese 15 fehlten. Vier davon (`circle`,
  // `circle-check`, `circle-minus`, `image`) erreichte man beim Merge nur ueber BERECHNETE Namen -
  // keine stand damals als Zeichenkette im JSX, deshalb fehlten sie in der Zaehlung des Merge-Berichts
  // (dort 11 statt 15). NACHTRAG (Auftrag "Zahlung anstoßen ..." Asana Task 1218171668075282,
  // 2026-09-04): `circle-check` steht seither woertlich in Bestellzeile.tsx:477 (`case "payment"`) -
  // eine spaetere Ergaenzung, die den urspruenglichen Merge-Befund nicht ruecklaeufig macht.
  // Keiner der 15 endet auf eine Ziffer, also kein Fall der Ziffernregel unten.
  //
  // Reiterleiste (`DRAWER_TAB_ICONS`) und Kopf: `clipboard-list`, `list-checks`, `files`,
  // `credit-card`, `calculator`, `x` stehen bereits oben. NACHGETRAGEN 2026-08-20: `truck` fuer den
  // neuen Reiter "Spedition" (`ship`) - siehe `drawerRechte.ts#DRAWER_TAB_ICONS` und
  // `docs/superpowers/decisions/2026-08-20-reiter-spedition.md` (steht unten bei den restlichen
  // Symbolen dieses Blocks, alphabetisch einsortiert).
  banknote: Banknote, // Zahlung: Vorlage "Betrag"
  blocks: Blocks, // Tasks: Phasenkopf
  circle: Circle, // Checkliste/Zahlung: offener Zustand (Gegenstueck zu `circle-check`)
  "circle-check": CircleCheck, // Checkliste: erledigt - NICHT `circle-check-big`
  "circle-minus": CircleMinus, // Checkliste: optionaler, nicht erledigter Eintrag
  "file-spreadsheet": FileSpreadsheet, // Dokumente: Bestell-Excel (`documentIconName`)
  "file-text": FileText, // Dokumente: Vorgabe von `documentIconName`
  "grip-vertical": GripVertical, // Positionen: Ziehgriff der Zeile
  history: History, // Dokumente: Verlauf
  image: ImageSymbol, // Dokumente: Bilddatei (`documentIconName`) - `Image` waere der DOM-Konstruktor
  "list-ordered": ListOrdered, // Zahlung: Vorlagenliste
  mail: Mail, // Zahlung: Zahlungsavis
  "scan-search": ScanSearch, // Dokumente: Sammel-Auslesen
  "scan-text": ScanText, // Checkliste: Auslesen einer Zeile
  send: Send, // Zahlung: Freigabe anstossen
  truck: Truck, // Reiterleiste: Reiter "Spedition" (`ship`, nachgetragen 2026-08-20)
  // Zeitstrahl-Seite (Task 6, Verdrahtungsplan Phase 6): die vier `data-lucide`-Platzhalter dieser
  // Flaeche (einzige Fundstellen im ganzen `src/`, siehe Task-Bericht) - `x` steht bereits oben.
  check: Check, // Stationsspur: Haken im erledigten Stufenknoten (`.tlp-dot`)
  search: Search, // Suchfeld-Praefix (`ZeitstrahlSuche.tsx`)
  // Feature "See/Flug-Umschaltung" (Spec 2026-08-26-spedition-see-flug-design.md, E6): `plane`
  // kennzeichnet das neue Flug-Badge in `ZeitstrahlSeite.tsx`. `ship` steht bereits oben und ist
  // davon unabhaengig bereits im Einsatz - ueber `config/board.ts` (Statuspille "Import") -, kommt
  // in `ZeitstrahlSeite.tsx` selbst aber nicht vor.
  plane: Plane,
  // Lieferstatus-Icons im Reiter "Anlieferung" (Auftrag "Lieferstatus-Icons + Uhrzeit", 2026-08-28):
  // `LieferAmpel` (Anlieferungsliste.tsx) braucht ein drittes Symbol fuer "teilgeliefert" (gestrichelter
  // Kreis) zwischen `circle` ("offen", steht bereits oben) und `circle-check-big` ("komplett", steht
  // ebenfalls bereits oben) - `CircleDashed` endet nicht auf eine Ziffer, also kein Fall der
  // Ziffernregel unten.
  "circle-dashed": CircleDashed,
  // Seite "Zahlungsvorlagen": Info-Kasten ("Aenderungen wirken nur auf kuenftige Uebernahmen").
  info: Info,
};

/**
 * Symbole mit Ziffernende - siehe BEFUND im Kopfkommentar. Jedes `iconNode` woertlich aus
 * `node_modules/lucide-react/dist/esm/icons/<name>.js` (v0.468.0).
 */
const ZIFFERN_SYMBOLE: Record<string, IconNode> = {
  "clock-3": [
    ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }],
    ["polyline", { points: "12 6 12 12 16.5 12", key: "1aq6pp" }],
  ],
  "columns-3": [
    ["rect", { width: "18", height: "18", x: "3", y: "3", rx: "2", key: "afitv7" }],
    ["path", { d: "M9 3v18", key: "fh3hqa" }],
    ["path", { d: "M15 3v18", key: "14nvp0" }],
  ],
  "settings-2": [
    ["path", { d: "M20 7h-9", key: "3s1dr2" }],
    ["path", { d: "M14 17H5", key: "gfn3mx" }],
    ["circle", { cx: "17", cy: "17", r: "3", key: "18b49y" }],
    ["circle", { cx: "7", cy: "7", r: "3", key: "dfmy0x" }],
  ],
  // Task 14: der dritte Fall der Ziffernregel. `Trash2` -> `toKebabCase("Trash2")` = `trash2`,
  // also `lucide-trash2`; die Baselines `board-spalteneditor` und `board-listeneditor` fuehren
  // `lucide-trash-2`. Task 22 braucht dasselbe Symbol fuer "Beleg entfernen" und
  // "Sicherung loeschen" (Baseline `payments-standard`), Task 15 fuer das Kontextmenue.
  "trash-2": [
    ["path", { d: "M3 6h18", key: "d0wm0j" }],
    ["path", { d: "M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6", key: "4alrt4" }],
    ["path", { d: "M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2", key: "v07s0e" }],
    ["line", { x1: "10", x2: "10", y1: "11", y2: "17", key: "1uufr5" }],
    ["line", { x1: "14", x2: "14", y1: "11", y2: "17", key: "xtxkd" }],
  ],
};

export interface IkoneProps {
  /** Der Name, den `icon(...)` im Original bekommt - z. B. `"arrow-right"` oder `"trash-2"`. */
  name: string;
  /** Das zweite Argument von `icon(name, className)` - z. B. `"status-icon"`. */
  className?: string;
}

export function Ikone({ name, className }: IkoneProps) {
  const knoten = ZIFFERN_SYMBOLE[name];
  if (knoten) {
    return (
      <Icon
        iconNode={knoten}
        className={className ? `lucide-${name} ${className}` : `lucide-${name}`}
        strokeWidth={STRICHSTAERKE}
        aria-hidden="true"
      />
    );
  }
  const Symbol = KOMPONENTEN[name];
  // Ein unbekannter Name waere im Original ein `<i data-lucide="…">`, das lucide stehen laesst -
  // sichtbar nichts. Hier ebenso nichts, statt eines Absturzes.
  if (!Symbol) return null;
  return <Symbol className={className} strokeWidth={STRICHSTAERKE} aria-hidden="true" />;
}
